import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	geSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-next/schema';
import {ShareLinkField, type IShareLink} from './shareLinkSchema';

export const ACCESS_TOKEN_SORTABLE_FIELDS = [
	ShareLinkField.assetCount,
	ShareLinkField.title,
	ShareLinkField.creationDate,
	ShareLinkField.expirationDate,
	ShareLinkField.lastDownloadedDate,
	ShareLinkField.userLabel,
	ShareLinkField.status,
] as const;

export const ShareLinksSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(ACCESS_TOKEN_SORTABLE_FIELDS),
});

export type ShareLinksSort = z.infer<typeof ShareLinksSortSchema>;

export const ShareLinksSortableField = z.enum(
	ACCESS_TOKEN_SORTABLE_FIELDS,
).enum;

export const ACCESS_TOKENS_DEFAULT_SORT_FIELD =
	ShareLinksSortableField.creationDate;

export const ACCESS_TOKENS_DEFAULT_SORT_ORDER = SortOrder.DESCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getShareLinksDefaultSort = (
	sort: Partial<ShareLinksSort> = {},
): ShareLinksSort => ({
	field: getShareLinksSortField({sortField: sort.field}),
	order: getShareLinksSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getShareLinksSortField = (
	params: Omit<
		GetSortFieldParams<typeof ACCESS_TOKEN_SORTABLE_FIELDS[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: ACCESS_TOKEN_SORTABLE_FIELDS,
		defaultSortField: ACCESS_TOKENS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getShareLinksSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	geSortOrder({
		...params,
		defaultSortOrder: ACCESS_TOKENS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const shareLinkEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IShareLink,
			typeof ACCESS_TOKEN_SORTABLE_FIELDS[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: ACCESS_TOKEN_SORTABLE_FIELDS,
	});
